import React from "react";
import Container from "@/components/marketing/Container";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { ArrowRight, TrendingUp, Eye, Layers, Zap, Gauge, Boxes } from "lucide-react";
import SectionHeading from "@/components/marketing/SectionHeading";
import ServiceHero from "@/components/marketing/service/ServiceHero";
import Reveal from "@/components/marketing/Reveal";

const BENEFITS = [
  { icon: TrendingUp, title: "Margin on every order", description: "Reseller rates are priced so there is room to mark up. You set your client price; the spread is yours." },
  { icon: Eye, title: "Your brand, not ours", description: "Everything your client sees carries your identity. Unbranded reports, no Digital24 branding, and we never contact your clients." },
  { icon: Layers, title: "A bigger catalogue overnight", description: "1,700+ outlets, 23 newswire networks, and guaranteed placements your clients can order tomorrow, without you building a single supplier relationship." },
  { icon: Zap, title: "Less operational drag", description: "Order, track, and download reports self-serve. No email chains, no chasing suppliers, no spreadsheets." },
  { icon: Gauge, title: "Speed your clients notice", description: "Same-day newswire distribution and defined turnarounds you can safely promise in your own proposals." },
  { icon: Boxes, title: "One supplier instead of ten", description: "Placements, newswires, and outreach fulfilled through one account, one invoice, one point of contact." },
];

const EXPECTATIONS = [
  { title: "Custom packages", text: "Our team builds reseller packages around the outlets, newswires, and volumes you need." },
  { title: "Custom pricing", text: "Rates set against your order profile and commitments. The more you order, the deeper your rate." },
  { title: "Bulk order discounts", text: "Bulk orders earn an immediate discount on the platform." },
  { title: "API access", text: "Programmatic order placement and status tracking for high-volume partners." },
  { title: "Named account management", text: "A named account manager who knows your catalogue and keeps your orders moving." },
];

export default function Resellers() {
  return (
    <div className="bg-[#f7f7f9]">
      {/* Hero */}
      <ServiceHero
        eyebrow="RESELLERS & PARTNERS"
        title="White-Label PR Distribution for Resellers"
        subtitle="Offer press release distribution and guaranteed placements under your own brand. Wholesale pricing, white-label reporting, and a self-serve platform for your team."
        primaryCTA={{ label: "Apply for Reseller Access", to: "/resellers/apply" }}
      />

      {/* Benefits + Tiers */}
      <section className="py-12 lg:py-16">
        <Container className="relative">
          {/* Benefits */}
          <Reveal>
            <SectionHeading tag="Benefits" title="Why Resellers Choose Digital24" />
          </Reveal>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8 mb-24 lg:mb-32">
            {BENEFITS.map((b, i) => (
              <Reveal key={b.title} variant="card" delay={i * 0.08}>
              <div
                className="bg-white border-[0.5px] border-[#e8e6ef] rounded-[14px] p-8 hover:border-[#d8d5e2] hover:shadow-md transition-all"
              >
                <div
                  className="inline-flex items-center justify-center w-12 h-12 rounded-xl mb-5"
                  style={{ backgroundColor: "rgba(114,0,203,0.10)" }}
                >
                  <b.icon className="w-6 h-6 text-[#7200CB]" strokeWidth={1.75} />
                </div>
                <h3 className="text-[#14121c] text-lg font-semibold mb-2">{b.title}</h3>
                <p className="text-[#3a3646] text-[15px] leading-[1.6]">{b.description}</p>
              </div>
              </Reveal>
            ))}
          </div>

          {/* What to Expect — split section: anchor left, ledger right */}
          <Reveal>
          <div className="grid lg:grid-cols-5 gap-10 lg:gap-16">
            {/* Left anchor column */}
            <div className="lg:col-span-2">
              <span className="meta-tag inline-block mb-5">WHAT TO EXPECT</span>
              <h2 className="font-heading text-3xl sm:text-4xl font-bold text-[#14121c] leading-tight mb-5">
                Pricing built around you, not a rate card
              </h2>
              <p className="text-[#3a3646] text-[15px] leading-[1.7] mb-8">
                No flat tiers. Approved resellers get a commercial setup shaped around what they sell and how much of it. Pricing is agreed at approval, based on what you plan to resell.
              </p>
              <Link to="/resellers/apply">
                <Button size="lg" className="bg-cobalt hover:bg-cobalt/90 text-white">
                  Apply for Reseller Access <ArrowRight className="ml-2 w-4 h-4" />
                </Button>
              </Link>
            </div>

            {/* Right ledger column — five open rows, hairline dividers, no card boxes */}
            <div className="lg:col-span-3">
              <div className="divide-y divide-[#e8e6ef] border-t border-[#e8e6ef]">
                {EXPECTATIONS.map((e, i) => (
                  <div key={e.title} className="flex items-start gap-6 py-6">
                    <span className="font-heading text-[28px] font-bold text-[#d8d5e2] leading-none shrink-0 w-10">
                      {String(i + 1).padStart(2, "0")}
                    </span>
                    <div>
                      <h3 className="text-[#14121c] text-[17px] font-semibold mb-1">{e.title}</h3>
                      <p className="text-[#3a3646] text-[15px] leading-[1.6]">{e.text}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
          </Reveal>
        </Container>
      </section>

      {/* Closing CTA band — the only full-purple surface on the page */}
      <Reveal>
      <section className="relative bg-cobalt overflow-hidden">
        <div
          className="absolute inset-0 opacity-[0.06] pointer-events-none"
          style={{
            backgroundImage:
              "linear-gradient(rgba(255,255,255,0.6) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.6) 1px, transparent 1px)",
            backgroundSize: "60px 60px",
          }}
        />
        <Container className="relative py-10 sm:py-12 text-center">
          <span className="font-mono text-xs uppercase tracking-widest text-white/80 inline-block mb-6">
            GET STARTED TODAY
          </span>
          <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-bold text-white leading-tight mb-5">
            Put our catalogue behind your brand.
          </h2>
          <p className="text-white/80 text-base lg:text-lg max-w-2xl mx-auto mb-10 leading-relaxed">
            Wholesale rates, white-label delivery, and a platform built for repeat orders.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <Link to="/resellers/apply">
              <Button size="lg" className="bg-white hover:bg-white/90 text-cobalt font-medium px-8">
                Apply for Reseller Access <ArrowRight className="ml-2 w-4 h-4" />
              </Button>
            </Link>
            <Link to="/contact">
              <Button
                variant="outline"
                size="lg"
                className="border-white text-white bg-transparent hover:bg-white/10 hover:text-white px-8"
              >
                Talk to Our Team
              </Button>
            </Link>
          </div>
        </Container>
      </section>
      </Reveal>
    </div>
  );
}